import React from 'react'
import _ from 'underscore'
import moment from 'moment-timezone'

import styled, { css } from 'styled-components'

import { ILegend, LegendItem, legendDataForTimeAtCursor } from 'types/RuntimeHistory'
import { color } from 'styles/colors'

import AlertsLegendGroup from 'pages/Device/ThermostatRthPanel/Legend/AlertsLegendGroup'
import LegendGroup from 'pages/Device/ThermostatRthPanel/Legend/LegendGroup'
import IndoorLegendGroup from 'pages/Device/ThermostatRthPanel/Legend/IndoorLegendGroup'
import LegendConfig from 'pages/Device/ThermostatRthPanel/Legend/legendItemsConfig'

import { screenSize } from 'styles/shared'
import { Alert, DeviceConnectionState } from 'types/Alert'

import useNexiaStream from 'hooks/useNexiaStream'
import { useWebsocketContext } from 'hooks/WebsocketContext'
import { useFeaturesContext } from 'hooks/FeaturesContext'

type Props = {
  timeAtCursor: number | null
  legendData: Required<ILegend>
  timeZone: string
  isChartHovered: boolean
  isServerlessRth?: boolean
  isControlHub: boolean
  isHehm?: boolean
  legendDataForTimeAtCursor: legendDataForTimeAtCursor | null
  alertsInVisibleRange: Array<Alert>
  deviceConnectionStatusAlerts: Array<DeviceConnectionState>
  selectedLegends: {
    series: Array<string>,
    zoneSeries: Array<{ name: string, series: Array<string> }>,
    alerts: Array<string>
  },
  deviceId: string
  reportCache: object
  isLegendItemHasData: (l: LegendItem, d: ILegend) => boolean
  onClickAlertsLegendItem: (i) => void
  onClickAlertsLegendAllOrNone: (i, n) => void
  onIndoorLegendItemClickCallback: (i, n) => void
  onLegendItemClickCallback: (i, n?) => void
  onIndoorLegendItemSelectAllClickCallback: (i, n) => void
  onLegendItemSelectAllClickCallback: (i, n) => void
  onIndoorZoneClickCallback: (series, zoneName) => void
}

const Legend = ({
  timeAtCursor,
  legendData,
  isChartHovered,
  isControlHub,
  isHehm = false,
  isServerlessRth = false,
  timeZone,
  legendDataForTimeAtCursor,
  selectedLegends,
  alertsInVisibleRange,
  deviceConnectionStatusAlerts,
  deviceId,
  reportCache,
  isLegendItemHasData,
  onClickAlertsLegendItem,
  onClickAlertsLegendAllOrNone,
  onIndoorZoneClickCallback,
  onIndoorLegendItemSelectAllClickCallback,
  onLegendItemSelectAllClickCallback,
  onLegendItemClickCallback,
  onIndoorLegendItemClickCallback
}: Props) => {
  // HEHM uses its own simplified legend groups
  const hehmOutdoorItems = LegendConfig.HEHM_OUTDOOR_LEGEND_ITEMS
  const hehmIndoorItems = LegendConfig.HEHM_INDOOR_LEGEND_ITEMS

  const { payload: systemConfigReport } = useWebsocketContext(deviceId, 'system_config')
  const { enabledFeatures } = useFeaturesContext()
  const { eventData: report } = useNexiaStream(deviceId, 'sys_config', reportCache)
  const isWebsocketEnabled = enabledFeatures.includes('montblanc_websockets')
  const actualReport = isWebsocketEnabled ? systemConfigReport?.systemConfig : report

  let indoorUnitLegendItems: Array<LegendItem>
  if (isHehm) {
    indoorUnitLegendItems = hehmIndoorItems
  } else if (actualReport?.indoorUnitType === 'GAS_OIL') {
    indoorUnitLegendItems = _.union(LegendConfig.GAS_FURNACE_INDOOR_LEGEND_ITEMS, isControlHub ? LegendConfig.CONTROL_HUB_GAS_FURNACE_INDOOR_LEGEND_ITEMS : [])
  } else if (isControlHub) {
    indoorUnitLegendItems = _.union(LegendConfig.INDOOR_UNIT_LEGEND_ITEMS, LegendConfig.CONTROL_HUB_INDOOR_UNIT_LEGEND_ITEMS)
  } else {
    indoorUnitLegendItems = LegendConfig.INDOOR_UNIT_LEGEND_ITEMS
  }

  let indoorLegendItems: Array<LegendItem> | null = null
  if (!isHehm) {
    indoorLegendItems = legendData.zones.length > 1 ? LegendConfig.INDOOR_ZONE_LEGEND_ITEMS : LegendConfig.INDOOR_LEGEND_ITEMS
  }

  const outdoorLegendItems = isHehm ? hehmOutdoorItems : LegendConfig.OUTDOOR_LEGEND_ITEMS
  const outdoorUnitLegendItems = isHehm ? [] : LegendConfig.OUTDOOR_UNIT_LEGEND_ITEMS

  _.each(indoorUnitLegendItems, (legendItem) => { legendItem.emptyDataSet = !isLegendItemHasData(legendItem, legendData) })
  _.each(outdoorLegendItems, (legendItem) => { legendItem.emptyDataSet = !isLegendItemHasData(legendItem, legendData) })
  _.each(outdoorUnitLegendItems, (legendItem) => { legendItem.emptyDataSet = !isLegendItemHasData(legendItem, legendData) })

  const shouldDisplayLegendGroup = (legendGroup) => _.any(legendGroup, (item) => !item.emptyDataSet)
  const displayIndoorUnitGroup = shouldDisplayLegendGroup(indoorUnitLegendItems)
  const displayOutdoorGroup = shouldDisplayLegendGroup(outdoorLegendItems)
  const displayOutdoorUnitGroup = shouldDisplayLegendGroup(outdoorUnitLegendItems)

  const selectedOutdoorUnitLegends = _.filter(selectedLegends.series, (seriesName) => _.any(outdoorUnitLegendItems, item => item.seriesName === seriesName))
  const selectedOutdoorLegends = _.filter(selectedLegends.series, (seriesName) => _.any(outdoorLegendItems, item => item.seriesName === seriesName))
  const selectedIndoorUnitLegends = _.filter(selectedLegends.series, (seriesName) => _.any(indoorUnitLegendItems, item => item.seriesName === seriesName))

  return (
    <S.Legend {...{ isChartHovered, isHehm }}>
      {isChartHovered && <S.Time>{moment(timeAtCursor).tz(timeZone || moment.tz.guess()).format('h:mm A')}</S.Time>}

      <S.LegendGroupContainer>
        {!isHehm && indoorLegendItems && legendData.zones && (
          <IndoorLegendGroup
            {...{ legendDataForTimeAtCursor }}
            legendItems={indoorLegendItems}
            zones={legendData.zones}
            onIndoorLegendItemClickCallback={onIndoorLegendItemClickCallback}
            onIndoorLegendSelectAllCallback={onIndoorLegendItemSelectAllClickCallback}
            onIndoorZoneClickCallback={onIndoorZoneClickCallback}
            selectedLegends={selectedLegends.zoneSeries}
          />
        )}

        {displayIndoorUnitGroup && (
          <LegendGroup {...{ legendDataForTimeAtCursor }}
            onLegendItemClickCallback={onLegendItemClickCallback}
            onLegendItemSelectAllClickCallback={onLegendItemSelectAllClickCallback}
            label="Indoor Unit"
            isControlHub={isControlHub}
            isHehm={isHehm}
            legendItems={indoorUnitLegendItems}
            selectedLegends={selectedIndoorUnitLegends}
          />
        )}

        <S.OutdoorLegendGroupContainer>
          {displayOutdoorGroup && (
            <LegendGroup {...{ legendDataForTimeAtCursor }}
              onLegendItemClickCallback={onLegendItemClickCallback}
              onLegendItemSelectAllClickCallback={onLegendItemSelectAllClickCallback}
              label={isHehm ? 'Outdoor Unit' : 'Outdoor'}
              isHehm={isHehm}
              legendItems={outdoorLegendItems}
              selectedLegends={selectedOutdoorLegends}
              showCompressorSpeedInOutdoor={!displayOutdoorUnitGroup}
            />
          )}

          {displayOutdoorUnitGroup && (
            <LegendGroup {...{ legendDataForTimeAtCursor }}
              onLegendItemClickCallback={onLegendItemClickCallback}
              onLegendItemSelectAllClickCallback={onLegendItemSelectAllClickCallback}
              label="Outdoor Unit"
              legendItems={outdoorUnitLegendItems}
              selectedLegends={selectedOutdoorUnitLegends}
            />
          )}
        </S.OutdoorLegendGroupContainer>

        <AlertsLegendGroup
          alerts={alertsInVisibleRange}
          deviceConnectionStatusAlerts={deviceConnectionStatusAlerts}
          selectedLegends={selectedLegends.alerts || []}
          isServerlessRth={isServerlessRth}
          isHehm={isHehm}
          onClickLegendItem={onClickAlertsLegendItem}
          onClickAllOrNone={onClickAlertsLegendAllOrNone}
        />
      </S.LegendGroupContainer>
    </S.Legend>
  )
}

export default Legend

const S = {
  Legend: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isChartHovered', 'isHehm'].includes(prop)
  })<{ isChartHovered: boolean, isHehm?: boolean }>`
    padding-top: 25px;
    padding-bottom: 20px;
    padding-left: 10px;
    margin: -26px 34px 0 34px;
    min-height: ${({ isHehm }) => isHehm ? '290px' : '651px'};
    overflow: hidden;

    ${({ isChartHovered }) => {
    if (isChartHovered) {
      return css`
          margin: -25px 34px -1px 34px;
          position: relative;
          border-radius: 5px;
          box-shadow:
            4px 4px 4px -4px ${color('rthLegendBorderHover')},
            4px 4px 4px -4px ${color('rthLegendBorderHover')},
            -4px 4px 4px -4px ${color('rthLegendBorderHover')},
            -4px -4px 4px -4px ${color('rthLegendBorderHover')},
            4px -4px 4px -4px ${color('rthLegendBorderHover')};
        `
    }
  }}
  `,
  LegendGroupContainer: styled.div`
    display: flex;
    flex-flow: row wrap;
  `,
  OutdoorLegendGroupContainer: styled.div`
    display: flex;
    flex-direction: column;

    @media ${screenSize.medium} {
      flex-direction: row;
    }
  `,
  Time: styled.div`
    width: 100%;
    margin-top: -30px;
    margin-bottom: -11px;
    font-weight: bold;
    font-size: 27px;
    text-align: center;
    color: ${color('rthLegendTime')};
  `
}
